import {copilotDockedPanelClosedEvent} from '@github-ui/copilot-chat/utils/constants'
import {
  publishOpenCopilotChat,
  subscribeAddCopilotChatReference,
  subscribeOpenCopilotChat,
} from '@github-ui/copilot-chat/utils/copilot-chat-events'
import {CopilotChatIntents} from '@github-ui/copilot-chat-types'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {CopilotIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useEffect, useEffectEvent, useRef} from 'react'

import {DockedPanel, useDockedPanelActions, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'

export function OpenCopilotChatPanelButton({
  size = 'small',
  variant = 'invisible',
  className,
}: {
  size?: 'small' | 'medium'
  variant?: 'invisible' | 'default'
  className?: string
}) {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.CopilotChat)
  const actions = useDockedPanelActions()
  const togglePanelRef = useRef<HTMLButtonElement>(null)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  // Close the embedded immersive panel on mount so chat only shows
  // in the side panel on the PR files view. Skip when the side panel
  // is already open — its own ready event suppresses the immersive
  // panel, and dispatching the close event here would race with it,
  // causing DockedCopilotChatPortal to re-open chat after clearing
  // isDockedPanelActive, leaving both panels visible.
  const dispatchClosedOnMount = useEffectEvent(() => {
    if (!isDockedPanelOpen) {
      document.dispatchEvent(new CustomEvent(copilotDockedPanelClosedEvent))
    }
  })
  useEffect(() => {
    dispatchClosedOnMount()
  }, [])

  // Intercept open-copilot-chat events so chat opens in the side panel
  // instead of the immersive embedded overlay on the PR files view.
  useEffect(() => {
    return subscribeOpenCopilotChat(() => {
      if (!isDockedPanelOpen) {
        actions.openPanel(DockedPanel.CopilotChat, {returnFocusTo: togglePanelRef.current})
      }
    })
  }, [actions, isDockedPanelOpen])

  // Intercept add-copilot-chat-reference events that request panel opening
  // so the reference is attached in the docked side panel instead of the
  // embedded overlay.
  useEffect(() => {
    return subscribeAddCopilotChatReference(e => {
      if (e.openPanel && !isDockedPanelOpen) {
        actions.openPanel(DockedPanel.CopilotChat, {returnFocusTo: togglePanelRef.current})
      }
    })
  }, [actions, isDockedPanelOpen])

  const buttonAriaLabel = isDockedPanelOpen ? 'Close Copilot panel' : 'Open Copilot panel'

  return (
    <div className={`d-flex flex-items-center${className ? ` ${className}` : ''}`}>
      <ErrorBoundary
        boundaryName="OpenCopilotChatPanelButton"
        fallback={
          <IconButton
            inactive
            aria-label="The Copilot panel cannot currently be opened."
            icon={CopilotIcon}
            size={size}
            variant={variant}
          />
        }
      >
        <IconButton
          ref={togglePanelRef}
          aria-label={buttonAriaLabel}
          aria-expanded={isDockedPanelOpen}
          icon={CopilotIcon}
          size={size}
          variant={variant}
          onClick={() => {
            sendPullRequestAnalyticsEvent('copilot_chat_side_panel.open', 'COPILOT_CHAT_SIDE_PANEL_BUTTON')
            if (isDockedPanelOpen) {
              actions.closePanel()
            } else {
              // Fire the standard open event so handleOpenPanelEvent sets up
              // chat state (chatIsOpen, thread selection) before the panel DOM
              // mounts. The subscribeOpenCopilotChat handler above will open
              // the panel. This avoids a flash of stale thread content.
              publishOpenCopilotChat({intent: CopilotChatIntents.conversation})
            }
          }}
        />
      </ErrorBoundary>
    </div>
  )
}
